import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Alert, Button, Form } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import * as XLSX from "xlsx";
import {
  getIndicatorSupportMceSummaryByEa,
  getIndicatorSupportMceSummaryByRegionalDelegation,
} from "../../../../../rest/indicator";
import {
  handleError,
  isNotBusinessError,
} from "../../../../../utils/handleError";
import Loading from "../../../../../components/general/Loading";
import { currencyFormat } from "../../../../../utils/CurrencyUtils";
import ExportDialog from "../utils/ExportDialog";
import LastUpdate from "../utils/LastUpdate";
import { buildWorkbookMCESupport } from "../sectionsIndicatorsEntities/exporters/exporterIndicatorMCESupport";
import KpiStat from "../utils/KpiStat";
import { useClientPdf } from "../utils/useClientPdf";

export default function IndicatorMCESupport() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [summaryEa, setSummaryEa] = useState([]);
  const [summaryRegional, setSummaryRegional] = useState([]);

  const [lastUpdateEa, setLastUpdateEa] = useState(null);
  const [lastUpdateReg, setLastUpdateReg] = useState(null);
  const [versionEa, setVersionEa] = useState(null);
  const [versionReg, setVersionReg] = useState(null);

  const [showExport, setShowExport] = useState(false);
  // Esta secção mostra apenas MCE. O Apoio Prévio e o Parecer têm secções próprias.
  const activeGroup = "mce";
  const [limitEa, setLimitEa] = useState(8);

  const [queryEa, setQueryEa] = useState("");
  const [sortEaBy, setSortEaBy] = useState("mcePaidValue");
  const [sortEaDir, setSortEaDir] = useState("desc");

  const [queryReg, setQueryReg] = useState("");
  const [sortRegBy, setSortRegBy] = useState("mcePaidValue");
  const [sortRegDir, setSortRegDir] = useState("desc");

  const { pdfRef, exportPdf } = useClientPdf({
    marginMm: 10,
    orientation: "portrait",
    hideSelectors: ['[data-hide-on-export="true"]', ".modal", ".toast"],
  });

  useEffect(() => {
    const rowsFrom = (payload) =>
      Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.results)
        ? payload.results
        : Array.isArray(payload)
        ? payload
        : [];
    const load = async () => {
      try {
        setLoading(true);
        const [eaRes, regRes] = await Promise.all([
          getIndicatorSupportMceSummaryByEa(),
          getIndicatorSupportMceSummaryByRegionalDelegation(),
        ]);
        const eaPayload = eaRes?.data || {};
        const regPayload = regRes?.data || {};

        const eaList = rowsFrom(eaPayload).map((r) => ({
          eaId: r?.eaUserId ?? r?.eaId ?? r?.id ?? null,
          eaName: r?.eaName ?? r?.description ?? r?.name ?? "-",
          mceContracted: Number(r?.mceContracted ?? 0),
          mcePaidCount: Number(r?.mcePaidCount ?? 0),
          mcePaidValue: Number(r?.mcePaidValue ?? 0),
        }));

        const regList = rowsFrom(regPayload).map((r) => ({
          regionalDelegationId: r?.regionalDelegationId ?? r?.id ?? null,
          regionalDelegationName: r?.regionalDelegationName ?? r?.name ?? "-",
          mceContracted: Number(r?.mceContracted ?? 0),
          mcePaidCount: Number(r?.mcePaidCount ?? 0),
          mcePaidValue: Number(r?.mcePaidValue ?? 0),
          eaCount: Number(r?.eaCount ?? 0),
        }));

        setSummaryEa(eaList);
        setSummaryRegional(regList);

        setLastUpdateEa(
          eaPayload.lastRefreshedAt ?? eaPayload.lastUpdated ?? null
        );
        setLastUpdateReg(
          regPayload.lastRefreshedAt ?? regPayload.lastUpdated ?? null
        );
        setVersionEa(eaPayload.refreshVersion ?? null);
        setVersionReg(regPayload.refreshVersion ?? null);
      } catch (err) {
        if (isNotBusinessError(err)) setError(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const fmtInt = (n) =>
    typeof n === "number" ? n.toLocaleString("pt-PT") : "0";
  const hasEa = summaryEa?.length > 0;
  const hasRegional = summaryRegional?.length > 0;
  const hasAnyData = hasEa || hasRegional;

  const groupDefs = {
    mce: {
      title: "MCE (3 anos)",
      fields: [
        { key: "mceContracted", label: "MCE Contratualizado", fmt: fmtInt },
        { key: "mcePaidCount", label: "N.º Relatórios Pagos", fmt: fmtInt },
        { key: "mcePaidValue", label: "Relatórios Pagos", fmt: currencyFormat },
      ],
      defaultSort: "mcePaidValue",
    },
  };

  useEffect(() => {
    setSortEaBy(groupDefs[activeGroup].defaultSort);
    setSortEaDir("desc");
    setLimitEa(8);
  }, [activeGroup]);

  const valorKey = groupDefs[activeGroup].fields[2].key;

  const rowsEaFiltered = useMemo(() => {
    const q = queryEa.trim().toLowerCase();
    if (!q) return summaryEa || [];
    return (summaryEa || []).filter((r) =>
      String(r?.eaName || "")
        .toLowerCase()
        .includes(q)
    );
  }, [summaryEa, queryEa]);

  const collator = useMemo(
    () => new Intl.Collator("pt-PT", { sensitivity: "base", numeric: true }),
    []
  );

  const rowsEaSorted = useMemo(() => {
    const arr = [...rowsEaFiltered];
    arr.sort((a, b) => {
      if (sortEaBy === "eaName") {
        const cmp = collator.compare(
          String(a?.eaName || ""),
          String(b?.eaName || "")
        );
        return sortEaDir === "asc" ? cmp : -cmp;
      }
      const av = Number(a?.[sortEaBy] ?? 0);
      const bv = Number(b?.[sortEaBy] ?? 0);
      if (av === bv) {
        const cmp = collator.compare(
          String(a?.eaName || ""),
          String(b?.eaName || "")
        );
        return sortEaDir === "asc" ? cmp : -cmp;
      }
      return sortEaDir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [rowsEaFiltered, sortEaBy, sortEaDir, collator]);

  const maxValorEa = useMemo(
    () => Math.max(1, ...rowsEaFiltered.map((r) => Number(r?.[valorKey] ?? 0))),
    [rowsEaFiltered, valorKey]
  );

  const rowsRegionalFiltered = useMemo(() => {
    const q = queryReg.trim().toLowerCase();
    if (!q) return summaryRegional || [];
    return (summaryRegional || []).filter((r) =>
      String(r?.regionalDelegationName || "")
        .toLowerCase()
        .includes(q)
    );
  }, [summaryRegional, queryReg]);

  const rowsRegionalSorted = useMemo(() => {
    const arr = [...rowsRegionalFiltered];
    arr.sort((a, b) => {
      if (sortRegBy === "regionalDelegationName") {
        const cmp = collator.compare(
          String(a?.regionalDelegationName || ""),
          String(b?.regionalDelegationName || "")
        );
        return sortRegDir === "asc" ? cmp : -cmp;
      }
      const av = Number(a?.[sortRegBy] ?? 0);
      const bv = Number(b?.[sortRegBy] ?? 0);
      if (av === bv) {
        const cmp = collator.compare(
          String(a?.regionalDelegationName || ""),
          String(b?.regionalDelegationName || "")
        );
        return sortRegDir === "asc" ? cmp : -cmp;
      }
      return sortRegDir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [rowsRegionalFiltered, sortRegBy, sortRegDir, collator]);

  const maxValorRegional = useMemo(
    () =>
      Math.max(
        1,
        ...rowsRegionalFiltered.map((r) => Number(r?.mcePaidValue ?? 0))
      ),
    [rowsRegionalFiltered]
  );

  const kpiTop = useMemo(() => {
    const [f1, f2, f3] = groupDefs[activeGroup].fields;
    return rowsEaFiltered.reduce(
      (acc, r) => {
        acc.a += Number(r?.[f1.key] ?? 0);
        acc.b += Number(r?.[f2.key] ?? 0);
        acc.c += Number(r?.[f3.key] ?? 0);
        return acc;
      },
      { a: 0, b: 0, c: 0 }
    );
  }, [rowsEaFiltered, activeGroup]);

  const numEntidadesEa = rowsEaFiltered.length;

  const barWrap = {
    height: 6,
    background: "#eef1f4",
    borderRadius: 999,
    overflow: "hidden",
  };
  const bar = (pct) => ({
    width: `${Math.min(100, Math.max(0, pct))}%`,
    height: "100%",
    background: "#0d6efd",
  });

  const handleExportExcel = async () => {
    try {
      const wb = await buildWorkbookMCESupport();
      XLSX.writeFile(wb, "apoios_mce.xlsx");
      setTimeout(() => setShowExport(false), 0);
    } catch (e) {
      console.error("[Excel Apoios MCE] falhou:", e);
      alert("Falha ao gerar Excel: " + (e?.message || e));
    }
  };

  const unifiedLastUpdateIso = useMemo(() => {
    const a = lastUpdateEa ? new Date(lastUpdateEa).getTime() : null;
    const b = lastUpdateReg ? new Date(lastUpdateReg).getTime() : null;
    const ts = a && b ? Math.max(a, b) : a || b || null;
    return ts ? new Date(ts).toISOString() : null;
  }, [lastUpdateEa, lastUpdateReg]);

  const unifiedVersion = useMemo(() => {
    const pick = (iso, ver) =>
      iso ? { ts: new Date(iso).getTime(), ver } : null;
    const ea = pick(lastUpdateEa, versionEa);
    const reg = pick(lastUpdateReg, versionReg);
    if (ea && reg) return ea.ts >= reg.ts ? ea.ver : reg.ver;
    return ea?.ver ?? reg?.ver ?? null;
  }, [lastUpdateEa, versionEa, lastUpdateReg, versionReg]);

  if (loading) return <Loading />;
  if (error) return handleError(error);

  return (
    <div className="container-fluid" ref={pdfRef}>
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="text-secondary mb-0">
            <FormattedMessage
              id="indicators.supportMce.title"
              defaultMessage="Apoios de MCE"
            />
          </h4>
          <div className="text-muted">{groupDefs[activeGroup].title}</div>
        </Col>
        <Col className="text-end">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (hasAnyData) setShowExport(true);
            }}
            aria-disabled={!hasAnyData}
            style={{
              color: "#e5533d",
              textDecoration: "underline",
              pointerEvents: hasAnyData ? "auto" : "none",
              opacity: hasAnyData ? 1 : 0.5,
            }}
            data-hide-on-export="true"
          >
            <FormattedMessage
              id="indicator.exportar"
              defaultMessage="Exportar"
            />
          </a>
        </Col>
      </Row>

      {hasEa && (
        <Row className="align-items-center g-3 mb-3">
          <Col xs={12} md={6}>
            <Form.Control
              value={queryEa}
              placeholder="Pesquisar por Entidade"
              onChange={(e) => setQueryEa(e.target.value)}
            />
          </Col>
          <Col xs={7} md={3}>
            <Form.Select
              value={sortEaBy}
              onChange={(e) => setSortEaBy(e.target.value)}
            >
              <option value="eaName">Ordenar por Entidade</option>
              {groupDefs[activeGroup].fields.map((f) => (
                <option
                  key={f.key}
                  value={f.key}
                >{`Ordenar por ${f.label}`}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={5} md={3}>
            <Form.Select
              value={sortEaDir}
              onChange={(e) => setSortEaDir(e.target.value)}
            >
              <option value="desc">Direção Descendente</option>
              <option value="asc">Direção Ascendente</option>
            </Form.Select>
          </Col>
        </Row>
      )}

      <Row className="g-3 mb-3">
        <Col xs={12} md={3}>
          <KpiStat label="N.º de Entidades" value={fmtInt(numEntidadesEa)} />
        </Col>
        {groupDefs[activeGroup].fields.map((f, idx) => {
          const val = ["a", "b", "c"][idx];
          const shown = f.key.endsWith("Value")
            ? currencyFormat(kpiTop[val])
            : fmtInt(kpiTop[val]);
          return (
            <Col key={f.key} xs={12} md={3}>
              <KpiStat label={f.label} value={shown} />
            </Col>
          );
        })}
      </Row>

      {!hasEa ? (
        <Alert variant="light" className="border mb-4">
          <i className="fa-regular fa-circle-info me-2" />
          Sem dados para apresentar.
        </Alert>
      ) : (
        <>
          <Row className="g-3 mb-4">
            {rowsEaSorted.slice(0, limitEa).map((r, idx) => {
              const [f1, f2, f3] = groupDefs[activeGroup].fields;
              const valor = Number(r?.[f3.key] ?? 0);
              const pct = Math.min(100, Math.round((valor / maxValorEa) * 100));
              return (
                <Col key={r.eaId ?? idx} xs={12} md={6}>
                  <Card className="shadow-sm h-100">
                    <Card.Body className="py-3">
                      <div className="fw-semibold mb-2">{r.eaName}</div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">{f1.label}</span>
                        <strong>{f1.fmt(r?.[f1.key] ?? 0)}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">{f2.label}</span>
                        <strong>{f2.fmt(r?.[f2.key] ?? 0)}</strong>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-1 mb-1">
                        <span className="text-muted">{f3.label}</span>
                        <strong>{f3.fmt(valor)}</strong>
                      </div>
                      <div style={barWrap}>
                        <div style={bar(pct)} />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>

          {rowsEaSorted.length > 8 && (
            <div className="text-center mb-4">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() =>
                  setLimitEa((n) => (n === 8 ? rowsEaSorted.length : 8))
                }
              >
                {limitEa === 8 ? "Ver todos" : "Mostrar menos"}
              </Button>
            </div>
          )}
        </>
      )}

      <Row className="mb-2">
        <Col>
          <h5 className="text-muted mb-0">Delegação Regional</h5>
          <div className="text-muted">
            MCE por delegação (contratualizados, pagos e valor pago).
          </div>
        </Col>
      </Row>

      {hasRegional && (
        <Row className="align-items-center g-3 mb-2">
          <Col xs={12} md={6}>
            <Form.Control
              value={queryReg}
              placeholder="Pesquisar por Delegação"
              onChange={(e) => setQueryReg(e.target.value)}
            />
          </Col>
          <Col xs={7} md={4}>
            <Form.Select
              value={sortRegBy}
              onChange={(e) => setSortRegBy(e.target.value)}
            >
              <option value="regionalDelegationName">
                Ordenar por Delegação
              </option>
              <option value="mcePaidValue">Ordenar por Relatórios Pagos</option>
              <option value="mcePaidCount">Ordenar por N.º Relatórios Pagos</option>
              <option value="mceContracted">
                Ordenar por MCE Contratualizado
              </option>
              <option value="eaCount">Ordenar por N.º de EAs</option>
            </Form.Select>
          </Col>
          <Col xs={5} md={2}>
            <Form.Select
              value={sortRegDir}
              onChange={(e) => setSortRegDir(e.target.value)}
            >
              <option value="desc">Direção Descer</option>
              <option value="asc">Direção Subir</option>
            </Form.Select>
          </Col>
        </Row>
      )}

      {!hasRegional ? (
        <Alert variant="light" className="border">
          <i className="fa-regular fa-circle-info me-2" />
          Sem dados para apresentar.
        </Alert>
      ) : (
        <Row className="g-3">
          {rowsRegionalSorted.map((r, idx) => {
            const v = Number(r?.mcePaidValue ?? 0);
            const pct = Math.min(100, Math.round((v / maxValorRegional) * 100));
            return (
              <Col key={r.regionalDelegationId ?? idx} xs={12} md={6}>
                <Card className="shadow-sm h-100">
                  <Card.Body className="py-3">
                    <div className="fw-semibold mb-3">
                      {r.regionalDelegationName}
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">MCE Contratualizado</span>
                      <strong>{fmtInt(r.mceContracted || 0)}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">N.º Relatórios Pagos</span>
                      <strong>{fmtInt(r.mcePaidCount || 0)}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-1 mb-1">
                      <span className="text-muted">Relatórios Pagos</span>
                      <strong>{currencyFormat(v)}</strong>
                    </div>
                    <div style={barWrap}>
                      <div style={bar(pct)} />
                    </div>
                    <div className="text-muted mt-2">
                      N.º de EAs:{" "}
                      <strong className="text-body">
                        {fmtInt(r.eaCount || 0)}
                      </strong>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <LastUpdate
        iso={unifiedLastUpdateIso}
        version={unifiedVersion}
        className="mt-3"
      />

      <ExportDialog
        show={showExport}
        onHide={() => setShowExport(false)}
        options={[
          { onClick: handleExportExcel, filename: "apoios_mce.xlsx" },
          {
            onClick: () => exportPdf("apoios_mce_ea_resumo.pdf"),
            filename: "apoios_mce_ea_resumo.pdf",
          },
        ]}
      />
    </div>
  );
}